// JARVIS Multi-Agent Orchestrator
// Runs 5 specialized Groq calls sequentially.
// Each agent gets only the context it needs — focused, fast, accurate.

import { apiUrl } from './apiBase';
import {
  RISK_AGENT_PROMPT,
  NEWS_AGENT_PROMPT,
  TRADE_MANAGER_PROMPT,
} from '../prompts/agentPrompts';
import { getFullJarvisContext, getTrades } from './storage';
import { getDayOfWeek } from '../utils/dateUtils';
import { analyzeAllTimeframes } from './scriptAnalysis';

// Steps 1 (Market Analyst) and 5 (Master Decision) used to ask Groq to
// INVENT htf_bias/structure/entry/SL/TP and a "confidence: 0-100" number
// via free-form prompts — exactly the fabrication problem found and fixed
// across Dashboard/Chat/LiveMonitor this session, and a direct violation
// of "Groq for chat only, never trade decisions" (Sir confirmed this rule
// explicitly). Both steps now use analyzeAllTimeframes() — same real
// Structure+Fibonacci+ADX math, zero AI, zero guessing. Risk Manager/News/
// Trade Manager stay Groq-assisted — they REASON over real given facts
// (account settings, real open trades) rather than inventing market state.

const MODEL = process.env.REACT_APP_CLAUDE_MODEL || 'claude-sonnet-4-6';

// ─── Single agent call ────────────────────────────────────────────────────────
const callAgent = async (agentName, systemPrompt, userMsg, maxTokens = 800) => {
  const res = await fetch(apiUrl('/api/chat'), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: maxTokens,
      system:     systemPrompt,
      messages:   [{ role: 'user', content: userMsg }],
    }),
  });

  const data = await res.json();
  if (!res.ok || data.error) {
    throw new Error(`${agentName}: ${data.error?.message || data.error || `HTTP ${res.status}`}`);
  }

  const raw = data.content[0].text.trim()
    .replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '');

  try {
    return JSON.parse(raw);
  } catch {
    return { raw_response: raw, parse_error: true };
  }
};

// ─── Main orchestrator ────────────────────────────────────────────────────────
export const runMultiAgentAnalysis = async (userQuestion, onStep) => {
  const ctx        = getFullJarvisContext();
  const allTrades  = getTrades();
  const day        = getDayOfWeek();
  const hourIST    = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', hour12: false });

  const agentCtx = {
    currentPrice:   ctx.currentPrice,
    currentSession: ctx.currentSession,
    riskSettings:   ctx.riskSettings,
    activeOBs:      (ctx.ict?.order_blocks || []).filter(o => o.status === 'Active').slice(0, 5),
    openFVGs:       (ctx.ict?.fvg || []).filter(f => f.status === 'Open').slice(0, 5),
    recentMS:       (ctx.ict?.market_structure || []).slice(-5),
    behaviors:      ctx.behaviors?.slice(0, 10) || [],
    patterns:       ctx.patterns?.slice(0, 5)   || [],
    openTrades:     allTrades.filter(t => t.result === 'OPEN'),
  };

  const results = {};

  // ── Step 1: Market Analyst — REAL math, not Groq ──────────────────────
  onStep?.({ step: 1, name: 'Market Analyst', status: 'running' });
  try {
    const tfResults = await analyzeAllTimeframes();
    const firm = tfResults.filter(r => r.ok && r.signal?.action !== 'WAIT');
    const anyValid = tfResults.find(r => r.ok && r.signal?.structure?.valid);
    results.market = {
      htf_bias: anyValid ? anyValid.signal.structure.trend : 'NEUTRAL',
      structure_state: firm.length ? 'BOS_CONFIRMED, in OTE zone' : anyValid ? 'Structure confirmed, not in entry zone yet' : 'No confirmed structure',
      market_summary: firm.length
        ? `${firm.length} timeframe(s) वर खरा setup: ${firm.map(f => `${f.tf} ${f.signal.action}`).join(', ')}`
        : 'सध्या कुठल्याही validated timeframe वर सगळे gates (structure+OTE+ADX+HTF+confirmation) एकत्र जुळलेले नाहीत.',
      tfResults, // raw, used by Master Decision below — not shown directly
      source: 'A_Signal.js — real Structure+Fibonacci+ADX, zero AI',
    };
    onStep?.({ step: 1, name: 'Market Analyst', status: 'done', result: results.market });
  } catch (e) {
    results.market = { error: e.message };
    onStep?.({ step: 1, name: 'Market Analyst', status: 'error', error: e.message });
  }

  // ── Step 2: Risk Manager ──────────────────────────────────────────────
  onStep?.({ step: 2, name: 'Risk Manager', status: 'running' });
  try {
    results.risk = await callAgent(
      'Risk Manager',
      RISK_AGENT_PROMPT(agentCtx, results.market),
      'Assess risk parameters for the current market setup.',
    );
    onStep?.({ step: 2, name: 'Risk Manager', status: 'done', result: results.risk });
  } catch (e) {
    results.risk = { error: e.message };
    onStep?.({ step: 2, name: 'Risk Manager', status: 'error', error: e.message });
  }

  // ── Step 3: News Agent ────────────────────────────────────────────────
  onStep?.({ step: 3, name: 'News Agent', status: 'running' });
  try {
    results.news = await callAgent(
      'News Agent',
      NEWS_AGENT_PROMPT(day, parseInt(hourIST)),
      `Assess macro/news risk for XAU/USD on ${day} at ${hourIST}:00 IST.`,
      400,
    );
    onStep?.({ step: 3, name: 'News Agent', status: 'done', result: results.news });
  } catch (e) {
    results.news = { error: e.message };
    onStep?.({ step: 3, name: 'News Agent', status: 'error', error: e.message });
  }

  // ── Step 4: Trade Manager ─────────────────────────────────────────────
  onStep?.({ step: 4, name: 'Trade Manager', status: 'running' });
  try {
    results.tradeManager = await callAgent(
      'Trade Manager',
      TRADE_MANAGER_PROMPT(agentCtx.openTrades, agentCtx.currentPrice),
      'Review all open trades and provide management instructions.',
      500,
    );
    onStep?.({ step: 4, name: 'Trade Manager', status: 'done', result: results.tradeManager });
  } catch (e) {
    results.tradeManager = { error: e.message };
    onStep?.({ step: 4, name: 'Trade Manager', status: 'error', error: e.message });
  }

  // ── Step 5: Master Decision — REAL numbers, not Groq-invented ones ────
  // Picks the firm signal (if any) already computed in Step 1 — never asks
  // an LLM to produce entry/SL/TP/confidence. "grade: A" only appears when
  // a real signal reaches here, because reaching here means all 5 gates
  // (structure, OTE zone, ADX, HTF, confirmation candle) already passed —
  // it is not a separate guessed number.
  onStep?.({ step: 5, name: 'Master Decision', status: 'running' });
  try {
    const tfResults = results.market?.tfResults || [];
    const firm = tfResults.filter(r => r.ok && r.signal?.action !== 'WAIT');
    const best = firm.length ? firm.reduce((a, b) => (b.signal.rr > a.signal.rr ? b : a)) : null;

    results.master = {
      final_verdict: best ? best.signal.action : 'WAIT',
      grade: best ? 'A' : '—',
      timeframe: best ? best.tf : null,
      entry_zone: best ? `$${best.signal.entry}` : null,
      stop_loss: best ? best.signal.sl : null,
      tp1: best ? best.signal.target : null,
      risk_reward: best ? `1:${best.signal.rr}` : null,
      reasoning: best ? best.signal.reason : results.market?.market_summary || 'कुठल्याही timeframe वर setup नाही.',
      key_risks: results.risk?.risk_notes || [],
      immediate_action: best
        ? `${best.tf} — ${best.signal.action} Entry $${best.signal.entry}, SL $${best.signal.sl}, T1 $${best.signal.target}`
        : 'सध्या काहीही करू नका — real math confirms WAIT.',
      consensus_summary: {
        market: best ? 'CONFIRMED SETUP' : 'NO SETUP',
        risk:   results.risk?.risk_verdict || 'UNKNOWN',
        news:   results.news?.news_verdict || 'UNKNOWN',
        trades: results.tradeManager?.overall_portfolio_verdict || 'UNKNOWN',
      },
      source: 'A_Signal.js — real math across all validated timeframes, zero AI',
    };
    onStep?.({ step: 5, name: 'Master Decision', status: 'done', result: results.master });
  } catch (e) {
    results.master = { error: e.message };
    onStep?.({ step: 5, name: 'Master Decision', status: 'error', error: e.message });
  }

  return results;
};
